import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-[#E6F7F7]/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-slate-200/50 bg-white/80 backdrop-blur-sm relative z-10 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#00A3A1] to-[#E94E89]" />

        <CardHeader className="space-y-3 text-center pt-8 pb-6">
          <div className="mx-auto">
            <img src="/simplly_logo.png" alt="Simplly Logo" className="h-14 mx-auto object-contain" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              {submitted ? 'Check your inbox' : 'Reset your password'}
            </CardTitle>
            <CardDescription className="text-slate-500 mt-1">
              {submitted
                ? "We've sent you a link if your email is registered."
                : "Enter your email and we'll send you a reset link."}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          {submitted ? (
            <div className="space-y-5" data-testid="forgot-password-success">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <p className="font-medium">Reset link sent</p>
                  <p className="text-emerald-800/80 mt-1">
                    The link expires in 60 minutes. If you don't see the email, check your spam folder.
                  </p>
                </div>
              </div>
              <Link to="/login">
                <Button className="w-full h-11" variant="outline" data-testid="forgot-password-back-to-login">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="forgot-password-email-input"
                    type="email"
                    autoFocus
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="forgot-password-submit-button"
                disabled={loading || !email.trim()}
                className="w-full h-11 bg-gradient-to-r from-[#00A3A1] to-[#00908E] text-white font-medium shadow-lg"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>

              <div className="pt-4 text-center text-sm">
                <Link to="/login" className="text-slate-600 hover:text-[#00A3A1] inline-flex items-center gap-1">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
